'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { logger } from '@/src/logging/logger';
import { emitBrowserVoiceSignalingEvent } from './browser-voice-observability';
import { useAccess } from '@/src/access/access-context';
import { canWithScope } from '@/src/access/access-helpers';
import { getAuthNavigationHoldSnapshot } from '@/src/auth/auth-navigation-hold';
import type { BrowserCallingCapability, VoicePlatformStatus } from '../../voice-platform/api';
import {
  isCurrentEmployeeVoiceEvent,
} from '../../voice-platform/browser-calling-capability';

import { useContactCenterRealtimeContext } from '../realtime/contact-center-realtime-provider';
import type { ContactCenterVoiceCallChangedPayload } from '../realtime/contact-center-realtime-types';
import {
  createVoiceIdempotencyKey,
  VoiceRequestRejectedError,
  voiceApi,
} from './api';
import { useBrowserVoiceRuntime } from './browser-voice-runtime';
import { selectBrowserOriginatedCallFeature } from './browser-originated-call-feature';
import type {
  VoiceCallRecord,
  VoiceCallSession,
  VoiceBrowserPreparedPlacement,
  VoiceBrowserReadyPlacement,
  VoiceConnectorOption,
  VoiceDestination,
  VoiceDialContext,
  VoiceLine,
  VoicePhonePolicy,
  VoiceOriginateResult,
} from './types';
import {
  isBrowserPlacement,
  isReadyBrowserPlacement,
  isE164Voice,
  isSipDevicePlacement,
  isVoiceExtension,
  normalizeVoiceDestination,
  reconcileVoiceEndpointIdentity,
} from './types';

export type VoiceWorkspaceContextValue = {
  canCall: boolean;
  canCallExternal: boolean;
  canCallInternal: boolean;
  hasCallPermission: boolean;
  browserCalling: BrowserCallingCapability | null;
  canPostCall: boolean;
  integration: VoiceConnectorOption | null;
  lines: VoiceLine[];
  selectedLineId: string;
  setSelectedLineId: (lineId: string) => void;
  destination: VoiceDestination;
  setDestination: (destination: VoiceDestination) => void;
  dialContext: VoiceDialContext | null;
  session: VoiceCallSession | null;
  activeCalls?: VoiceCallSession[];
  policy: VoicePhonePolicy | null;
  policyLoading: boolean;
  loadError: string | null;
  actionError: string | null;
  busy: boolean;
  ownsBrowserPhone: boolean;
  isMuted: boolean;
  dialerOpen: boolean;
  notification: VoiceWorkspaceNotification | null;
  dismissNotification: () => void;
  openDialer: (context?: Partial<VoiceDialContext>) => void;
  closeDialer: () => void;
  cancelCallPreparation: () => void;
  checkPolicy: () => Promise<VoicePhonePolicy | null>;
  startCall: () => Promise<void>;
  setMuted: (muted: boolean) => void;
  hangUp: () => Promise<void>;
  savePostCall: (body: {
    notes?: string;
    disposition?: string;
    callback?: { title?: string; description?: string; dueDate: string; assignedTenantMembershipId?: string };
  }) => Promise<boolean>;
  completeCallListOutcome: (callListItemId: string) => void;
  refresh: () => Promise<void>;
};

export type VoiceWorkspaceNotification = {
  callSessionId: string;
  destination: VoiceDestination | null;
  state: ContactCenterVoiceCallChangedPayload['state'];
  assignment: 'UNASSIGNED' | 'TEAM';
};

export function isVoiceCallActionAvailable(
  endpointAvailable: boolean,
  browserPhoneTransitioning: boolean,
): boolean {
  return endpointAvailable && !browserPhoneTransitioning;
}

export function resolveVoiceDestinationCapabilities(
  browserCalling: BrowserCallingCapability | null,
  hasCallPermission: boolean,
): { canCallExternal: boolean; canCallInternal: boolean } {
  return {
    canCallInternal: Boolean(
      hasCallPermission && browserCalling?.canStartInternalCall === true,
    ),
    canCallExternal: Boolean(
      hasCallPermission && browserCalling?.canStartExternalCall === true,
    ),
  };
}

const VoiceWorkspaceContext = createContext<VoiceWorkspaceContextValue | null>(null);

export function VoiceWorkspaceContextProvider({ children, value }: { children: ReactNode; value: VoiceWorkspaceContextValue }) {
  return <VoiceWorkspaceContext.Provider value={value}>{children}</VoiceWorkspaceContext.Provider>;
}

function initialSession(connectorId: string, clientUserRef: string | null): VoiceCallSession {
  return {
    callSessionId: null,
    connectorId,
    clientUserRef,
    destination: null,
    destinationE164: null,
    state: 'IDLE',
    stateSequence: null,
    failureCategory: null,
    failureMessage: null,
    startedAt: null,
    lastEventAt: null,
    endpointDeviceId: null,
    endpointType: null,
    endpointSelectionReason: null,
    fromLineId: null,
    direction: 'OUTBOUND',
    answeredAt: null,
    endedAt: null,
    durationSeconds: null,
    notes: null,
    disposition: null,
    afterCallStatus: 'NOT_REQUIRED',
    missedStatus: 'NOT_MISSED',
  };
}

function sessionFromRecord(record: VoiceCallRecord): VoiceCallSession {
  return {
    ...record,
    callSessionId: record.callSessionId ?? record.id,
    connectorId: record.connectorId,
    clientUserRef: record.clientUserRef ?? null,
    destination: record.destination ?? (
      record.destinationE164
        ? { type: 'phone_number', e164Number: record.destinationE164 }
        : null
    ),
    destinationE164: record.destinationE164 ?? null,
    stateSequence: record.stateSequence ?? null,
    failureCategory: record.failureCategory ?? null,
    failureMessage: record.failureMessage ?? null,
    startedAt: record.startedAt ?? record.createdAt ?? null,
    lastEventAt: record.lastEventAt ?? record.updatedAt ?? null,
    endpointDeviceId: record.endpointDeviceId ?? null,
    endpointType: record.endpointType ?? null,
    endpointSelectionReason: record.endpointSelectionReason ?? null,
    callerName: record.direction === 'INTERNAL' ? record.agentName ?? null : null,
  };
}

export function reconcileRecoveredSession(
  current: VoiceCallSession | null,
  recovered: VoiceCallSession,
  currentClientUserRef: string | null = current?.clientUserRef ?? null,
): VoiceCallSession {
  const sameCall = Boolean(
    current?.callSessionId && current.callSessionId === recovered.callSessionId,
  );
  return {
    ...recovered,
    ...reconcileVoiceEndpointIdentity(
      current ?? {},
      recovered,
      sameCall,
      currentClientUserRef,
    ),
    endpointSelectionReason: recovered.endpointSelectionReason ?? (
      sameCall ? current?.endpointSelectionReason ?? null : null
    ),
  };
}

export function applyVoiceEvent(
  session: VoiceCallSession,
  payload: ContactCenterVoiceCallChangedPayload,
  currentClientUserRef: string | null = session.clientUserRef,
): VoiceCallSession {
  if (payload.connectorId !== session.connectorId) return session;

  const exactSameCall = session.callSessionId !== null &&
    session.callSessionId === payload.callSessionId;
  const differentCall = session.callSessionId !== null && payload.callSessionId !== session.callSessionId;
  if (differentCall) {
    const incomingAt = Date.parse(payload.lastEventAt ?? '');
    const currentAt = session.lastEventAt === null ? null : Date.parse(session.lastEventAt);
    const stale = Number.isNaN(incomingAt) ||
      (currentAt !== null && !Number.isNaN(currentAt) && incomingAt <= currentAt);
    if (stale) return session;
  } else if (session.stateSequence !== null && payload.stateSequence <= session.stateSequence) {
    return session;
  }

  const now = payload.lastEventAt;
  const incomingDestination = payload.destination ?? (
    payload.destinationE164
      ? { type: 'phone_number' as const, e164Number: payload.destinationE164 }
      : null
  );
  const endpointIdentity = reconcileVoiceEndpointIdentity(
    session,
    payload,
    !differentCall,
    currentClientUserRef,
  );
  return {
    ...session,
    callSessionId: payload.callSessionId,
    clientUserRef: exactSameCall
      ? payload.clientUserRef ?? session.clientUserRef
      : payload.clientUserRef,
    destination: incomingDestination,
    destinationE164: incomingDestination?.type === 'phone_number'
      ? incomingDestination.e164Number
      : payload.destinationE164?.trim() || null,
    direction: payload.direction ?? (
      incomingDestination?.type === 'extension'
        ? 'INTERNAL'
        : differentCall
          ? undefined
          : session.direction
    ),
    eventSchemaVersion: payload.eventSchemaVersion ?? (differentCall ? null : session.eventSchemaVersion ?? null),
    fromNumber: payload.fromNumber ?? (differentCall ? null : session.fromNumber ?? null),
    toNumber: payload.toNumber ?? (differentCall ? null : session.toNumber ?? null),
    lineLabel: payload.lineLabel ?? (differentCall ? null : session.lineLabel ?? null),
    callerPresentation: payload.callerPresentation ?? (differentCall ? null : session.callerPresentation ?? null),
    callerName: payload.callerName ?? (differentCall ? null : session.callerName ?? null),
    routeTargetType: payload.routeTargetType ?? (differentCall ? null : session.routeTargetType ?? null),
    routeTargetId: payload.routeTargetId ?? (differentCall ? null : session.routeTargetId ?? null),
    routeSlot: payload.routeSlot ?? (differentCall ? null : session.routeSlot ?? null),
    routeDecisionReason: payload.routeDecisionReason ?? (differentCall ? null : session.routeDecisionReason ?? null),
    fromLineId: payload.fromLineId,
    ...endpointIdentity,
    endpointSelectionReason: differentCall ? null : session.endpointSelectionReason,
    contactId: differentCall ? null : session.contactId,
    companyId: differentCall ? null : session.companyId,
    dealId: differentCall ? null : session.dealId,
    contactName: differentCall ? null : session.contactName,
    companyName: differentCall ? null : session.companyName,
    state: payload.state,
    stateSequence: payload.stateSequence,
    failureCategory: payload.failureCategory,
    failureMessage: payload.failureMessage,
    lastEventAt: now,
    startedAt: differentCall ? now : session.startedAt ?? now,
    answeredAt: payload.answeredAt ?? (
      payload.state === 'ANSWERED'
        ? differentCall ? now : session.answeredAt ?? now
        : differentCall ? null : session.answeredAt
    ),
    endedAt: ['ENDED', 'FAILED'].includes(payload.state)
      ? payload.endedAt ?? now
      : differentCall ? null : session.endedAt,
    durationSeconds: payload.durationSeconds ?? (differentCall ? null : session.durationSeconds),
    notes: differentCall ? null : session.notes,
    disposition: differentCall ? null : session.disposition,
    missedStatus: payload.missedStatus ?? (differentCall ? 'NOT_MISSED' : session.missedStatus),
    callListItemId: differentCall ? null : session.callListItemId,
    afterCallStatus: ['ENDED', 'FAILED'].includes(payload.state)
      ? (!differentCall && session.callListItemId ? 'PENDING' : 'NOT_REQUIRED')
      : differentCall ? 'NOT_REQUIRED' : session.afterCallStatus,
    participants: payload.participants ?? (differentCall ? undefined : session.participants),
    winningParticipantId: payload.winningParticipantId ?? (differentCall ? null : session.winningParticipantId ?? null),
    winningEndpointDeviceId: payload.winningEndpointDeviceId ?? (differentCall ? null : session.winningEndpointDeviceId ?? null),
    availableCommands: payload.availableCommands ?? (differentCall ? undefined : session.availableCommands),
  };
}

function hasCurrentBrowserDeviceLeg(
  session: Pick<VoiceCallSession, 'participants'>,
  currentClientUserRef: string | null | undefined,
): boolean {
  return Boolean(
    currentClientUserRef && session.participants?.some((participant) =>
      participant.role === 'device_leg' &&
      participant.endpointType === 'browser_webrtc' &&
      participant.handleUserRef === currentClientUserRef
    ),
  );
}

export function canControlVoiceSession(
  session: VoiceCallSession | null | undefined,
  currentClientUserRef: string | null | undefined,
  activeBrowserCallIdentity: {
    connectorId: string;
    clientUserRef: string;
    endpointDeviceId: string;
  } | null | undefined,
  browserCallActive: boolean,
): boolean {
  if (!session || !currentClientUserRef) return false;
  if (session.clientUserRef === currentClientUserRef) return true;
  if (
    session.state !== 'ANSWERED' ||
    session.availableCommands?.includes('hangup') !== true ||
    !browserCallActive ||
    !activeBrowserCallIdentity ||
    activeBrowserCallIdentity.connectorId !== session.connectorId ||
    activeBrowserCallIdentity.clientUserRef !== currentClientUserRef
  ) return false;
  return Boolean(session.participants?.some((participant) =>
    participant.role === 'device_leg' &&
    participant.state.toLowerCase() === 'answered' &&
    participant.endpointType === 'browser_webrtc' &&
    participant.endpointDeviceId === activeBrowserCallIdentity.endpointDeviceId &&
    participant.handleUserRef === currentClientUserRef &&
    /^(?:svp1|pcorr1)\.[A-Za-z0-9_-]{12,128}$/.test(participant.sipCorrelationId ?? '')
  ));
}

export function isOperatorVoiceEvent(
  integration: VoiceConnectorOption | null,
  payload: ContactCenterVoiceCallChangedPayload,
  tenantId: string | null,
  permissions: { canView: boolean; canViewAll: boolean } = { canView: false, canViewAll: false },
): boolean {
  if (!tenantId || payload.tenantId !== tenantId) return false;
  if (!integration || payload.connectorId !== integration.id) return false;
  if (integration.clientUserRef && payload.clientUserRef === integration.clientUserRef) return true;
  if (hasCurrentBrowserDeviceLeg(payload, integration.clientUserRef)) return true;
  if (payload.clientUserRef === null) return permissions.canView;
  return permissions.canViewAll;
}

function isActiveSession(session: VoiceCallSession | null): boolean {
  return Boolean(session && ['PREPARING_CALL', 'PREPARING_BROWSER', 'DIALING', 'RINGING', 'ANSWERED'].includes(session.state));
}

export function selectPrimaryVoiceCall(
  calls: VoiceCallSession[],
  currentCallSessionId: string | null,
): VoiceCallSession | null {
  const current = calls.find((call) => call.callSessionId === currentCallSessionId);
  if (current && isActiveSession(current)) return current;
  const priority: Record<string, number> = {
    ANSWERED: 0,
    RINGING: 1,
    DIALING: 2,
    PREPARING_BROWSER: 3,
    PREPARING_CALL: 4,
  };
  return [...calls].sort((left, right) => {
    const stateOrder = (priority[left.state] ?? 99) - (priority[right.state] ?? 99);
    if (stateOrder !== 0) return stateOrder;
    return Date.parse(right.lastEventAt ?? '') - Date.parse(left.lastEventAt ?? '');
  })[0] ?? null;
}

const browserRegistrationPollIntervalMs = 500;
const browserRegistrationTimeoutMs = 20_000;
const crmPersistenceRetryCount = 2;
const crmPersistenceRetryDelayMs = 250;
const hangUpRecoveryAttemptCount = 3;
const hangUpRecoveryDelayMs = 500;
const staleSignalingCallTimeoutMs = 5 * 60 * 1000;
const browserOwnershipMessage = 'Browser calling is active in another tab. Use that tab for call audio.';
const browserMuteErrorMessage = 'Could not update the browser microphone.';

type HangUpCommandResponse = Awaited<ReturnType<typeof voiceApi.hangUpCall>>;

type HangUpRecoveryResult =
  | { kind: 'command'; response: HangUpCommandResponse }
  | { kind: 'reconciled'; call: VoiceCallRecord }
  | { kind: 'unconfirmed'; error: unknown };

export function isStaleSignalingSession(
  session: VoiceCallSession | null,
  now = Date.now(),
): boolean {
  if (!session || !['DIALING', 'RINGING'].includes(session.state)) return false;
  const observedAt = Date.parse(session.lastEventAt ?? session.startedAt ?? '');
  return (
    Number.isFinite(observedAt) &&
    now - observedAt >= staleSignalingCallTimeoutMs
  );
}

export async function hangUpWithRecovery(input: {
  connectorId: string;
  callSessionId: string;
  hangUp?: typeof voiceApi.hangUpCall;
  getCall?: typeof voiceApi.getCall;
  attemptCount?: number;
  delayMs?: number;
  sleep?: (milliseconds: number) => Promise<void>;
}): Promise<HangUpRecoveryResult> {
  const hangUp = input.hangUp ?? voiceApi.hangUpCall;
  const getCall = input.getCall ?? voiceApi.getCall;
  const attemptCount = Math.max(
    1,
    input.attemptCount ?? hangUpRecoveryAttemptCount,
  );
  const delayMs = Math.max(0, input.delayMs ?? hangUpRecoveryDelayMs);
  const sleep =
    input.sleep ??
    ((milliseconds: number) =>
      new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds)));
  let lastError: unknown = new Error('Hang Up could not be confirmed.');

  for (let attempt = 0; attempt < attemptCount; attempt += 1) {
    try {
      return {
        kind: 'command',
        response: await hangUp({
          connectorId: input.connectorId,
          callSessionId: input.callSessionId,
        }),
      };
    } catch (error) {
      lastError = error;
    }

    try {
      const { call } = await getCall(input.callSessionId);
      if (
        call.callSessionId === input.callSessionId &&
        ['ENDED', 'FAILED'].includes(call.state)
      ) {
        return { kind: 'reconciled', call };
      }
    } catch {
      // The command error remains the useful diagnostic. A failed read is
      // expected while the API gateway itself is unavailable.
    }

    if (attempt + 1 < attemptCount) await sleep(delayMs * (attempt + 1));
  }
  return { kind: 'unconfirmed', error: lastError };
}

type VoiceDialAttempt = {
  connectorId: string;
  fingerprint: string;
  idempotencyKey: string;
  prepared?: VoiceBrowserPreparedPlacement;
  ready?: VoiceBrowserReadyPlacement;
  browserRuntimeStarted?: boolean;
  browserOriginated?: {
    sessionId: string;
    preparedCallId: string;
    correlationId: string;
  };
  callSessionId?: string;
  cancelled: boolean;
  cleanupPromise?: Promise<void>;
};

const recoverableBrowserRegistrationReasons = new Set([
  'browser_session_missing',
  'endpoint_unregistered',
  'endpoint_unavailable',
  'endpoint_stale',
]);

export function browserStatusDurablyInvalidatesAttempt(
  status: VoicePlatformStatus,
  attempt: Pick<VoiceDialAttempt, 'connectorId' | 'prepared'>,
): boolean {
  const prepared = attempt.prepared;
  if (!prepared) return false;
  if (
    status.lifecycleState !== 'ACTIVE' ||
    status.integrationId !== attempt.connectorId ||
    status.clientUserRef !== prepared.clientUserRef
  ) return true;

  const capability = status.browserCalling;
  const destinationAllowed = prepared.destination.type === 'extension'
    ? capability.canStartInternalCall
    : capability.canStartExternalCall;
  if (destinationAllowed) return false;

  return capability.reasonCodes.length === 0 || capability.reasonCodes.some(
    (reason) => !recoverableBrowserRegistrationReasons.has(reason),
  );
}

type BrowserRegistrationWaitOptions = {
  getStatus: typeof voiceApi.getBrowserSessionStatus;
  assertOwner: () => void;
  timeoutMs?: number;
  pollIntervalMs?: number;
  now?: () => number;
  sleep?: (milliseconds: number) => Promise<void>;
};

export class BrowserRegistrationTerminalError extends Error {
  readonly definitive = true;
}

class VoicePreparationCancelledError extends Error {}

class CrmPersistenceCallMismatchError extends Error {}

export async function waitForBrowserRegistration(
  sessionId: string,
  options: BrowserRegistrationWaitOptions,
): Promise<void> {
  const timeoutMs = options.timeoutMs ?? browserRegistrationTimeoutMs;
  const pollIntervalMs = options.pollIntervalMs ?? browserRegistrationPollIntervalMs;
  const now = options.now ?? Date.now;
  const sleep = options.sleep ?? ((milliseconds: number) =>
    new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds)));
  const deadline = now() + timeoutMs;

  while (true) {
    options.assertOwner();
    const observed = await options.getStatus(sessionId);
    options.assertOwner();
    if (observed.sessionId !== sessionId) {
      throw new BrowserRegistrationTerminalError('Spark VP returned a different Browser session.');
    }
    if (observed.status === 'registered') return;
    if (observed.status !== 'issued') {
      throw new BrowserRegistrationTerminalError(
        `Browser registration became ${observed.status.replaceAll('_', ' ')} before the call started.`,
      );
    }
    if (now() >= deadline) {
      throw new BrowserRegistrationTerminalError(
        'Browser registration was not observed before the call timeout. Retry this call attempt.',
      );
    }
    await sleep(Math.min(pollIntervalMs, Math.max(0, deadline - now())));
  }
}

function attemptFingerprint(input: {
  connectorId: string;
  destination: VoiceDestination;
  lineId: string;
  dialContext: VoiceDialContext | null;
}): string {
  return JSON.stringify([
    input.connectorId,
    input.destination,
    input.lineId,
    input.dialContext?.contactId ?? null,
    input.dialContext?.companyId ?? null,
    input.dialContext?.dealId ?? null,
    input.dialContext?.callListItemId ?? null,
    input.dialContext?.leaseToken ?? null,
  ]);
}

export function applyOriginatedResponse(
  session: VoiceCallSession | null,
  call: Pick<VoiceOriginateResult, 'callSessionId' | 'endpointDeviceId' | 'endpointType' | 'endpointSelectionReason'>,
): VoiceCallSession | null {
  if (!session) return session;
  if (session.callSessionId && session.callSessionId !== call.callSessionId) return session;
  const authoritativeState = session.stateSequence !== null;
  const now = new Date().toISOString();
  return {
    ...session,
    callSessionId: call.callSessionId,
    endpointDeviceId: call.endpointDeviceId,
    endpointType: call.endpointType,
    endpointSelectionReason: call.endpointSelectionReason,
    ...(authoritativeState ? {} : {
      state: 'DIALING' as const,
      startedAt: session.startedAt ?? now,
      lastEventAt: session.lastEventAt ?? now,
    }),
  };
}

export function VoiceWorkspaceProvider({ children }: { children: ReactNode }) {
  const { access, activeTenant } = useAccess();
  const { subscribeVoice } = useContactCenterRealtimeContext();
  const browserRuntime = useBrowserVoiceRuntime();
  const releaseBrowserRuntimeRef = useRef(browserRuntime.release);
  const endBrowserCallRef = useRef(browserRuntime.endCall);
  const isBrowserCallActiveRef = useRef(browserRuntime.isCallActive);
  const observeBrowserReadinessRef = useRef(browserRuntime.observeReadiness);
  const bindPreparedBrowserCallRef = useRef(browserRuntime.bindPreparedCall);
  const cancelPreparedBrowserCallRef = useRef(browserRuntime.cancelPreparedCall);
  const [integration, setIntegration] = useState<VoiceConnectorOption | null>(null);
  const [lines, setLines] = useState<VoiceLine[]>([]);
  const [selectedLineId, setSelectedLineId] = useState('');
  const [destination, setDestination] = useState<VoiceDestination>({
    type: 'phone_number',
    e164Number: '',
  });
  const [dialContext, setDialContext] = useState<VoiceDialContext | null>(null);
  const [session, setSession] = useState<VoiceCallSession | null>(null);
  const [activeCallsById, setActiveCallsById] = useState<Record<string, VoiceCallSession>>({});
  const [policy, setPolicy] = useState<VoicePhonePolicy | null>(null);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [notification, setNotification] = useState<VoiceWorkspaceNotification | null>(null);
  const [browserCalling, setBrowserCalling] = useState<BrowserCallingCapability | null>(null);
  const mountedRef = useRef(true);
  const sessionRef = useRef<VoiceCallSession | null>(null);
  const activeCallsRef = useRef<Record<string, VoiceCallSession>>({});
  const policyRequestRef = useRef(0);
  const muteErrorCallIdRef = useRef<string | null>(null);
  const hangUpPromiseRef = useRef<Promise<void> | null>(null);
  const attemptRef = useRef<VoiceDialAttempt | null>(null);
  const dismissedTerminalCallIdsRef = useRef(new Set<string>());
  const hasCallPermission = canWithScope(access, 'contact_center.voice.call', 'OWNED');
  const destinationCapabilities = resolveVoiceDestinationCapabilities(
    browserCalling,
    hasCallPermission,
  );
  // A browser-session rotation is not a safe time to let the server select
  // Browser WebRTC. The runtime keeps the predecessor alive, but the call
  // action remains unavailable until the exact registered successor is stable.
  const browserPhoneTransitioning = browserRuntime.isTransitioning;
  const canCallExternal = isVoiceCallActionAvailable(
    destinationCapabilities.canCallExternal,
    browserPhoneTransitioning,
  );
  const canCallInternal = isVoiceCallActionAvailable(
    destinationCapabilities.canCallInternal,
    browserPhoneTransitioning,
  );
  const canCall = canCallInternal || canCallExternal;
  const canPostCall = canWithScope(access, 'contact_center.voice.post_call', 'OWNED');
  const canView = canWithScope(access, 'contact_center.voice.view', 'OWNED');
  const canViewAll = canWithScope(access, 'contact_center.voice.view', 'ALL');
  const canObserveCalls = canView || hasCallPermission || canPostCall;

  useEffect(() => {
    releaseBrowserRuntimeRef.current = browserRuntime.release;
    endBrowserCallRef.current = browserRuntime.endCall;
    isBrowserCallActiveRef.current = browserRuntime.isCallActive;
    observeBrowserReadinessRef.current = browserRuntime.observeReadiness;
    bindPreparedBrowserCallRef.current = browserRuntime.bindPreparedCall;
    cancelPreparedBrowserCallRef.current = browserRuntime.cancelPreparedCall;
  }, [
    browserRuntime.bindPreparedCall,
    browserRuntime.cancelPreparedCall,
    browserRuntime.endCall,
    browserRuntime.isCallActive,
    browserRuntime.observeReadiness,
    browserRuntime.release,
  ]);

  const cleanupAttempt = useCallback((target: VoiceDialAttempt): Promise<void> => {
    target.cancelled = true;
    if (target.cleanupPromise) return target.cleanupPromise;
    const cleanup = (async () => {
      if (target.callSessionId) {
        await Promise.allSettled([
          voiceApi.hangUpCall({
            connectorId: target.connectorId,
            callSessionId: target.callSessionId,
          }),
          endBrowserCallRef.current(target.callSessionId),
        ]);
      } else if (target.browserOriginated) {
        await cancelPreparedBrowserCallRef.current(target.browserOriginated.preparedCallId);
      } else if (target.prepared) {
        await releaseBrowserRuntimeRef.current({
          connectorId: target.connectorId,
          sessionId: target.prepared.browserSession.sessionId,
        });
      }
      if (attemptRef.current === target) attemptRef.current = null;
    })();
    target.cleanupPromise = cleanup;
    return cleanup;
  }, []);

  const updateSession = useCallback((updater: (current: VoiceCallSession | null) => VoiceCallSession | null) => {
    const currentSession = sessionRef.current;
    const next = updater(currentSession);
    if (
      currentSession?.callSessionId &&
      isBrowserCallActiveRef.current(currentSession.callSessionId) &&
      next?.callSessionId !== currentSession.callSessionId
    ) {
      // A recovered/replacement call must not inherit the predecessor's local
      // media or mute state. Runtime release is the single teardown boundary.
      void releaseBrowserRuntimeRef.current();
    }
    if (
      muteErrorCallIdRef.current &&
      (next?.callSessionId !== muteErrorCallIdRef.current ||
        ['ENDED', 'FAILED', 'BLOCKED', 'IDLE'].includes(next.state))
    ) {
      muteErrorCallIdRef.current = null;
      setActionError((current) => current === browserMuteErrorMessage ? null : current);
    }
    sessionRef.current = next;
    setSession(next);
    return next;
  }, []);

  const assertBrowserOwner = useCallback(() => {
    if (!browserRuntime.isBrowserPhoneOwner()) throw new Error(browserOwnershipMessage);
  }, [browserRuntime]);

  const discardAttempt = useCallback((releasePrepared: boolean) => {
    const attempt = attemptRef.current;
    if (attempt) attempt.cancelled = true;
    attemptRef.current = null;
    if (releasePrepared && attempt?.prepared && !attempt.callSessionId) {
      void cleanupAttempt(attempt);
    }
  }, [cleanupAttempt]);

  const refresh = useCallback(async () => {
    if (getAuthNavigationHoldSnapshot()) return;
    const [statusResult, activeResult] = await Promise.allSettled([
      hasCallPermission ? voiceApi.getStatus() : Promise.resolve(null),
      canView ? voiceApi.getActiveCalls() : Promise.resolve({ calls: [] }),
    ]);
    if (!mountedRef.current || getAuthNavigationHoldSnapshot()) return;
    const recoveredCalls = activeResult.status === 'fulfilled'
      ? activeResult.value.calls.map(sessionFromRecord)
      : [];
    const recoveredById = Object.fromEntries(recoveredCalls.flatMap((call) =>
      call.callSessionId ? [[call.callSessionId, call]] : [],
    ));
    activeCallsRef.current = recoveredById;
    setActiveCallsById(recoveredById);
    const activeCall = selectPrimaryVoiceCall(
      recoveredCalls,
      sessionRef.current?.callSessionId ?? null,
    );
    const status = statusResult.status === 'fulfilled' ? statusResult.value : null;
    setBrowserCalling(status?.browserCalling ?? null);
    if (status?.browserCalling) observeBrowserReadinessRef.current(status.browserCalling);
    const destinationCapabilities = resolveVoiceDestinationCapabilities(
      status?.browserCalling ?? null,
      hasCallPermission,
    );
    const canCallExternalFromStatus = destinationCapabilities.canCallExternal;
    const attempt = attemptRef.current;
    if (
      status &&
      attempt?.prepared &&
      browserStatusDurablyInvalidatesAttempt(status, attempt)
    ) {
      attemptRef.current = null;
      void cleanupAttempt(attempt);
    }
    const active = status?.lifecycleState === 'ACTIVE' && status.integrationId
      ? {
          id: status.integrationId,
          displayName: status.displayName,
          handleId: '',
          defaultLineId: null,
          clientUserRef: status.clientUserRef,
        }
      : activeCall ? {
          id: activeCall.connectorId,
          displayName: 'Spark VP',
          handleId: '',
          defaultLineId: null,
          clientUserRef: activeCall.clientUserRef,
        } : null;
    setIntegration(active);
    if (activeCall) {
      const recovered = activeCall;
      updateSession((current) => reconcileRecoveredSession(
        current,
        recovered,
        active?.clientUserRef ?? null,
      ));
      setDestination(recovered.destination ?? {
        type: 'phone_number',
        e164Number: activeCall.destinationE164 ?? '',
      });
      setDialerOpen(true);
    } else if (active) {
      const idle = initialSession(active.id, active.clientUserRef);
      const current = sessionRef.current;
      const currentCallSessionId = current?.callSessionId ?? null;
      const providerDroppedCurrent = Boolean(
        activeResult.status === 'fulfilled' &&
        currentCallSessionId &&
        isActiveSession(current) &&
        !recoveredById[currentCallSessionId] &&
        attemptRef.current?.callSessionId !== currentCallSessionId
      );
      if (providerDroppedCurrent) {
        updateSession(() => idle);
        setDialerOpen(false);
        setDialContext(null);
        setNotification((value) =>
          value?.callSessionId === currentCallSessionId ? null : value,
        );
      } else {
        updateSession((value) => value ?? idle);
      }
    } else {
      updateSession(() => null);
    }

    if (active && canCallExternalFromStatus) {
      const lineResult = await Promise.allSettled([voiceApi.listLines(active.id)]);
      if (!mountedRef.current) return;
      const linesResult = lineResult[0];
      if (linesResult.status === 'fulfilled') {
        const available = linesResult.value.lines.filter((line) =>
          line.status.toLowerCase() === 'active' && line.isSelectableForOutbound && line.canOriginate,
        );
        setLines(available);
        setSelectedLineId((current) =>
          available.some((line) => line.lineId === current)
            ? current
            : available.find((line) => line.isDefaultOutbound)?.lineId || available[0]?.lineId || '',
        );
      } else {
        setLines([]);
        setSelectedLineId('');
        setLoadError(
          linesResult.reason instanceof Error
            ? linesResult.reason.message
            : 'Voice Lines could not be refreshed.',
        );
        return;
      }
    } else {
      setLines([]);
      setSelectedLineId('');
    }
    if (statusResult.status === 'rejected' && hasCallPermission) {
      setLoadError(statusResult.reason instanceof Error ? statusResult.reason.message : 'Voice calling setup is unavailable.');
      return;
    }
    if (activeResult.status === 'rejected' && canView) {
      setLoadError(activeResult.reason instanceof Error ? activeResult.reason.message : 'Active calls could not be recovered.');
      return;
    }
    setLoadError(null);
  }, [canView, cleanupAttempt, hasCallPermission, updateSession]);

  useEffect(() => {
    mountedRef.current = true;
    queueMicrotask(() => {
      void refresh().catch((error: unknown) => {
        if (mountedRef.current) setLoadError(error instanceof Error ? error.message : 'Phone workspace is unavailable.');
      });
    });
    return () => {
      mountedRef.current = false;
      const attempt = attemptRef.current;
      if (attempt) attempt.cancelled = true;
      attemptRef.current = null;
      if (attempt?.prepared && !attempt.callSessionId) void cleanupAttempt(attempt);
    };
  }, [cleanupAttempt, refresh]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    const terminal = browserRuntime.terminalEvent;
    if (!terminal) return;
    updateSession((current) => {
      if (
        !current ||
        current.callSessionId !== terminal.callSessionId ||
        !isActiveSession(current)
      ) return current;
      return {
        ...current,
        state: terminal.status === 'ended' ? 'ENDED' : 'FAILED',
        failureCategory: terminal.status === 'failed'
          ? 'BROWSER_SIP_FAILED'
          : current.failureCategory,
        failureMessage: terminal.status === 'failed'
          ? terminal.message ?? 'The browser phone connection failed.'
          : current.failureMessage,
        lastEventAt: terminal.occurredAt,
        endedAt: terminal.occurredAt,
        afterCallStatus: current.callListItemId ? 'PENDING' : 'NOT_REQUIRED',
      };
    });
    if (attemptRef.current?.callSessionId === terminal.callSessionId) {
      attemptRef.current = null;
      setBusy(false);
    }
  }, [browserRuntime.terminalEvent, updateSession]);

  useEffect(() => subscribeVoice({
    includeOrganization: canViewAll,
    onRecover: refresh,
    onEmployeeVoiceChanged: (payload) => {
      if (!activeTenant?.id || payload.tenantId !== activeTenant.id) return;
      if (!isCurrentEmployeeVoiceEvent(payload, integration?.clientUserRef)) return;
      void refresh().catch((error: unknown) => {
        if (mountedRef.current) {
          setLoadError(
            error instanceof Error
              ? error.message
              : 'Voice calling capability could not be refreshed.',
          );
        }
      });
    },
    onCallChanged: (payload) => {
      if (!isOperatorVoiceEvent(
        integration,
        payload,
        activeTenant?.id ?? null,
        { canView: canObserveCalls, canViewAll },
      )) return;
      const activeAttempt = attemptRef.current;
      const pendingBrowserOriginated = activeAttempt?.browserOriginated;
      const pendingCorrelationMatches = Boolean(
        pendingBrowserOriginated &&
        payload.participants?.some((participant) =>
          participant.role === 'caller' &&
          participant.endpointDeviceId ===
            (activeAttempt?.prepared ?? activeAttempt?.ready)?.endpointDeviceId &&
          participant.handleUserRef === integration?.clientUserRef &&
          participant.sipCorrelationId === pendingBrowserOriginated.correlationId,
        ),
      );
      // While a Browser-originated SIP authorization is pending, only its
      // public caller correlation may adopt or terminate the local attempt.
      // Same-user/same-endpoint events are insufficient and are ignored.
      if (
        activeAttempt &&
        !activeAttempt.callSessionId &&
        pendingBrowserOriginated &&
        !pendingCorrelationMatches
      ) return;
      let terminalBeforeBind = false;
      if (
        activeAttempt &&
        !activeAttempt.callSessionId &&
        pendingBrowserOriginated &&
        pendingCorrelationMatches &&
        (payload.direction === 'OUTBOUND' || payload.direction === 'INTERNAL')
      ) {
        // The participant's public correlation is the exact cross-repository
        // binding key. `preparedCallId` remains authorization-only and never
        // acquires call-session semantics in SparkOne.
        const bound = bindPreparedBrowserCallRef.current({
          sessionId: pendingBrowserOriginated.sessionId,
          preparedCallId: pendingBrowserOriginated.preparedCallId,
          correlationId: pendingBrowserOriginated.correlationId,
          callSessionId: payload.callSessionId,
        });
        if (bound) {
          activeAttempt.callSessionId = payload.callSessionId;
        } else if (['ENDED', 'FAILED'].includes(payload.state)) {
          // The provider's exact terminal event is authoritative even when the
          // local RTCSession has already become terminal and cannot bind. Mark
          // this attempt unavailable before asynchronous cleanup so no later
          // event can re-adopt it; cleanup remains limited to its prepared ID.
          terminalBeforeBind = true;
          activeAttempt.cancelled = true;
          if (attemptRef.current === activeAttempt) attemptRef.current = null;
          setBusy(false);
          void cleanupAttempt(activeAttempt).catch((error: unknown) => {
            logger.warn('Provider terminal pending Browser cleanup failed', {
              message: error instanceof Error ? error.message : String(error),
            });
          });
        }
      }
      const assignedToOperator = Boolean(
        integration?.clientUserRef && (
          payload.clientUserRef === integration.clientUserRef ||
          hasCurrentBrowserDeviceLeg(payload, integration.clientUserRef)
        ),
      );
      const assignedTeamCall = payload.clientUserRef !== null && canViewAll;
      const unassignedRinging = payload.clientUserRef === null && payload.state === 'RINGING';
      const terminal = ['ENDED', 'FAILED'].includes(payload.state);
      if (
        terminal &&
        dismissedTerminalCallIdsRef.current.has(payload.callSessionId)
      ) return;
      const current = sessionRef.current;
      const existing = activeCallsRef.current[payload.callSessionId];
      const reconciled = applyVoiceEvent(
        existing ?? (
          current?.callSessionId === payload.callSessionId
            ? current
            : initialSession(payload.connectorId, payload.clientUserRef)
        ),
        payload,
        integration?.clientUserRef ?? null,
      );
      const nextCalls = { ...activeCallsRef.current };
      if (terminal) delete nextCalls[payload.callSessionId];
      else nextCalls[payload.callSessionId] = reconciled;
      activeCallsRef.current = nextCalls;
      setActiveCallsById(nextCalls);
      const currentCallTerminal = Boolean(
        terminal && current?.callSessionId === payload.callSessionId,
      );
      if (currentCallTerminal) {
        // A provider terminal event is authoritative for the managed call.
        // Tear down only its already-bound local dialog; a stale or unrelated
        // event can never terminate a pending Browser-originated RTCSession.
        void endBrowserCallRef.current(payload.callSessionId).catch((error: unknown) => {
          logger.warn('Provider terminal Browser media cleanup failed', {
            callSessionId: payload.callSessionId,
            message: error instanceof Error ? error.message : String(error),
          });
        });
      }
      const primary = selectPrimaryVoiceCall(
        Object.values(nextCalls),
        current?.callSessionId ?? null,
      );
      const assignedToVisibleOperator = assignedToOperator || assignedTeamCall;
      const adopted = currentCallTerminal || (
        terminal
          ? terminalBeforeBind || (!isActiveSession(current) && assignedToVisibleOperator)
          : primary?.callSessionId === payload.callSessionId && (
            assignedToVisibleOperator || unassignedRinging
          )
      );
      if (adopted) {
        updateSession(() => reconciled);
        setDestination(payload.destination ?? (
          payload.destinationE164
            ? { type: 'phone_number', e164Number: payload.destinationE164 }
            : { type: 'phone_number', e164Number: '' }
        ));
        setDialerOpen(true);
      }
      if (
        terminal &&
        attemptRef.current?.callSessionId === payload.callSessionId
      ) {
          attemptRef.current = null;
        setBusy(false);
      } else if (
        activeAttempt?.callSessionId === payload.callSessionId &&
        ['DIALING', 'RINGING', 'ANSWERED'].includes(payload.state)
      ) {
        setBusy(false);
      }
      const missedTerminal = terminal && payload.missedStatus === 'UNRESOLVED';
      if (!adopted || missedTerminal) {
        setNotification({
          callSessionId: payload.callSessionId,
          destination: payload.destination ?? (
            payload.destinationE164
              ? { type: 'phone_number', e164Number: payload.destinationE164 }
              : null
          ),
          state: payload.state,
          assignment: payload.clientUserRef === null ? 'UNASSIGNED' : 'TEAM',
        });
      }
    },
  }), [activeTenant?.id, canObserveCalls, canViewAll, integration, refresh, subscribeVoice, updateSession]);

  const updateDestination = useCallback((nextDestination: VoiceDestination) => {
    if (JSON.stringify(nextDestination) !== JSON.stringify(destination)) discardAttempt(true);
    policyRequestRef.current += 1;
    setDestination(nextDestination);
    setPolicy(null);
  }, [destination, discardAttempt]);

  const updateSelectedLineId = useCallback((lineId: string) => {
    if (lineId !== selectedLineId) discardAttempt(true);
    setSelectedLineId(lineId);
    setPolicy(null);
  }, [discardAttempt, selectedLineId]);

  const openDialer = useCallback((context: Partial<VoiceDialContext> = {}) => {
    const nextDestination: VoiceDestination = context.destination?.type === 'phone_number'
      ? {
          type: 'phone_number',
          e164Number: normalizeVoiceDestination(context.destination.e164Number),
        }
      : context.destination?.type === 'extension'
        ? {
            type: 'extension',
            extensionNumber: context.destination.extensionNumber.trim(),
          }
        : destination;
    discardAttempt(true);
    const current = sessionRef.current;
    if (current?.callSessionId && ['ENDED', 'FAILED'].includes(current.state)) {
      if (current.callListItemId && current.afterCallStatus === 'PENDING') {
        setActionError('Complete the required call-list outcome before starting another call.');
        setDialerOpen(true);
        return;
      }
      dismissedTerminalCallIdsRef.current.add(current.callSessionId);
      updateSession(() => integration
        ? initialSession(integration.id, integration.clientUserRef)
        : current);
    }
    setDialContext({ ...context, destination: nextDestination });
    policyRequestRef.current += 1;
    setDestination(nextDestination);
    setActionError(null);
    setPolicy(null);
    setDialerOpen(true);
  }, [destination, discardAttempt, integration, updateSession]);

  const closeDialer = useCallback(() => {
    if (session && ['PREPARING_CALL', 'PREPARING_BROWSER', 'DIALING', 'RINGING', 'ANSWERED'].includes(session.state)) return;
    if (session?.callListItemId && session.afterCallStatus === 'PENDING') return;
    if (session?.callSessionId && ['ENDED', 'FAILED'].includes(session.state)) {
      dismissedTerminalCallIdsRef.current.add(session.callSessionId);
    }
    discardAttempt(true);
    setDialerOpen(false);
    setDialContext(null);
    setNotification((current) => current?.callSessionId === session?.callSessionId ? null : current);
    setActionError(null);
  }, [discardAttempt, session]);

  const cancelCallPreparation = useCallback(() => {
    const current = sessionRef.current;
    if (!current || !['PREPARING_CALL', 'PREPARING_BROWSER'].includes(current.state)) return;
    discardAttempt(true);
    setBusy(false);
    setActionError(null);
    updateSession((value) => value ? {
      ...value,
      callSessionId: null,
      state: 'IDLE',
      stateSequence: null,
      endpointDeviceId: null,
      endpointType: null,
      endpointSelectionReason: null,
      failureCategory: null,
      failureMessage: null,
      lastEventAt: null,
      startedAt: null,
      endedAt: null,
      afterCallStatus: 'NOT_REQUIRED',
    } : value);
  }, [discardAttempt, updateSession]);

  const checkPolicy = useCallback(async () => {
    if (destination.type !== 'phone_number') return null;
    const normalizedPhone = normalizeVoiceDestination(destination.e164Number);
    if (!isE164Voice(normalizedPhone)) return null;
    const requestId = ++policyRequestRef.current;
    setPolicyLoading(true);
    try {
      const result = await voiceApi.getPhonePolicy(normalizedPhone);
      if (requestId === policyRequestRef.current) {
        setPolicy(result.policy);
        return result.policy;
      }
      return null;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Calling policy could not be checked.';
      if (requestId === policyRequestRef.current) setActionError(message);
      return null;
    } finally {
      if (requestId === policyRequestRef.current) setPolicyLoading(false);
    }
  }, [destination]);

  useEffect(() => {
    if (!dialerOpen || destination.type !== 'phone_number') return;
    const normalizedPhone = normalizeVoiceDestination(destination.e164Number);
    if (!isE164Voice(normalizedPhone)) return;
    const timer = window.setTimeout(() => {
      void checkPolicy();
    }, 400);
    return () => window.clearTimeout(timer);
  }, [checkPolicy, destination, dialerOpen]);

  const startCall = useCallback(async () => {
    if (isActiveSession(sessionRef.current)) {
      setActionError('Finish the current call before starting another one.');
      return;
    }
    const destinationCanCall = destination.type === 'extension'
      ? canCallInternal
      : canCallExternal;
    if (!destinationCanCall && browserRuntime.isTransitioning) {
      setActionError('Connecting browser phone…');
      return;
    }
    if (!destinationCanCall) {
      setActionError(
        hasCallPermission && destination.type === 'extension'
          ? 'Your employee account is not ready for internal calls.'
          : hasCallPermission
            ? 'Your employee account is not ready for external calls.'
          : 'Your role cannot place voice calls.',
      );
      return;
    }
    if (!integration || !sessionRef.current) {
      setActionError('Voice calling is not available right now.');
      return;
    }
    const normalizedPhone = destination.type === 'phone_number'
      ? normalizeVoiceDestination(destination.e164Number)
      : null;
    if (destination.type === 'phone_number' && (!normalizedPhone || !isE164Voice(normalizedPhone))) {
      setActionError('Enter a valid phone number in E.164 format, starting with +.');
      return;
    }
    if (destination.type === 'extension' && !isVoiceExtension(destination.extensionNumber)) {
      setActionError('Enter a valid extension using 2 to 12 digits.');
      return;
    }
    if (destination.type === 'phone_number' && !selectedLineId) {
      setActionError('Choose an available Line before calling.');
      return;
    }
    if (destination.type === 'phone_number') {
      const effectivePolicy = policy ?? await checkPolicy();
      if (
        !effectivePolicy ||
        !effectivePolicy.allowed ||
        effectivePolicy.doNotCall ||
        ['NOT_CONSENTED', 'DO_NOT_CALL'].includes(effectivePolicy.consentStatus)
      ) {
        setActionError(effectivePolicy?.reasons[0] ?? 'This phone number is blocked by calling policy.');
        return;
      }
    }

    const normalizedDestination: VoiceDestination = destination.type === 'phone_number'
      ? { type: 'phone_number', e164Number: normalizedPhone! }
      : { type: 'extension', extensionNumber: destination.extensionNumber.trim() };
    const effectiveLineId = normalizedDestination.type === 'phone_number'
      ? selectedLineId
      : '';
    const fingerprint = attemptFingerprint({
      connectorId: integration.id,
      destination: normalizedDestination,
      lineId: effectiveLineId,
      dialContext,
    });
    let attempt = attemptRef.current;
    if (!attempt || attempt.fingerprint !== fingerprint) {
      discardAttempt(true);
      attempt = {
        connectorId: integration.id,
        fingerprint,
        idempotencyKey: createVoiceIdempotencyKey(),
        cancelled: false,
      };
      attemptRef.current = attempt;
    }

    const activePersistentRegistration =
      browserRuntime.getActivePersistentRegistration();
    const placementInput = {
      connectorId: integration.id,
      destination: normalizedDestination,
      ...(effectiveLineId ? { lineId: effectiveLineId } : {}),
      contactId: dialContext?.contactId,
      companyId: dialContext?.companyId,
      dealId: dialContext?.dealId,
      callListItemId: dialContext?.callListItemId,
      leaseToken: dialContext?.leaseToken,
      ...(activePersistentRegistration?.connectorId === integration.id
        ? {
            activeBrowserSessionId: activePersistentRegistration.sessionId,
            activationProof: activePersistentRegistration.activationProof,
          }
        : {}),
      idempotencyKey: attempt.idempotencyKey,
    };
    const originateInput = (
      browser: VoiceBrowserPreparedPlacement | VoiceBrowserReadyPlacement,
    ) => ({
      connectorId: browser.connectorId,
      webrtcSessionId: isReadyBrowserPlacement(browser)
        ? browser.webrtcSessionId
        : browser.browserSession.sessionId,
      ...(browser.lineId ? { lineId: browser.lineId } : {}),
      extensionId: browser.extensionId,
      endpointDeviceId: browser.endpointDeviceId,
      destination: browser.destination,
      contactId: dialContext?.contactId,
      companyId: dialContext?.companyId,
      dealId: dialContext?.dealId,
      callListItemId: dialContext?.callListItemId,
      leaseToken: dialContext?.leaseToken,
      idempotencyKey: attempt.idempotencyKey,
    });
    const assertAttemptCurrentAndOwned = () => {
      if (attempt.cancelled || attemptRef.current !== attempt) {
        throw new VoicePreparationCancelledError('Call preparation was cancelled.');
      }
      assertBrowserOwner();
    };
    const assertAttemptActive = () => {
      assertAttemptCurrentAndOwned();
      if (
        ((attempt.browserRuntimeStarted && attempt.prepared &&
          !browserRuntime.isSessionActive(attempt.prepared.browserSession.sessionId)) ||
          (attempt.ready &&
            !browserRuntime.isSessionActive(attempt.ready.webrtcSessionId)))
      ) {
        throw new VoicePreparationCancelledError('Call preparation was cancelled.');
      }
    };
    const retryPendingPersistence = async (
      initial: VoiceOriginateResult | Extract<Awaited<ReturnType<typeof voiceApi.placeCall>>, { phase: 'originated' }>,
      retry: () => Promise<VoiceOriginateResult | Extract<Awaited<ReturnType<typeof voiceApi.placeCall>>, { phase: 'originated' }>>,
    ) => {
      let result = initial;
      for (let index = 0; result.crmPersistenceState === 'pending' && index < crmPersistenceRetryCount; index += 1) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, crmPersistenceRetryDelayMs));
        assertAttemptActive();
        try {
          const next = await retry();
          if (next.callSessionId !== result.callSessionId) {
            await voiceApi.hangUpCall({
              connectorId: integration.id,
              callSessionId: next.callSessionId,
            }).catch((error: unknown) => {
              logger.warn('Mismatched CRM reconciliation call cleanup failed', {
                message: error instanceof Error ? error.message : String(error),
                callSessionId: next.callSessionId,
              });
            });
            throw new CrmPersistenceCallMismatchError(
              'SparkOne returned a different call while reconciling CRM state.',
            );
          }
          assertAttemptActive();
          result = next;
          updateSession((current) => applyOriginatedResponse(current, next));
        } catch (error) {
          if (
            error instanceof CrmPersistenceCallMismatchError ||
            (error instanceof Error && error.message === browserOwnershipMessage)
          ) throw error;
          logger.warn('Voice call CRM reconciliation retry failed', {
            message: error instanceof Error ? error.message : String(error),
            callSessionId: result.callSessionId,
          });
        }
      }
      if (result.crmPersistenceState === 'pending') {
        setActionError('The call started, but CRM state is still reconciling. Call controls remain available.');
      }
    };
    setBusy(true);
    setActionError(null);
    const previous = sessionRef.current;
    if (
      previous?.callSessionId &&
      ['ENDED', 'FAILED'].includes(previous.state) &&
      !previous.callListItemId &&
      previous.missedStatus !== 'UNRESOLVED'
    ) {
      dismissedTerminalCallIdsRef.current.add(previous.callSessionId);
      setNotification((current) => current?.callSessionId === previous.callSessionId ? null : current);
    }
    updateSession((current) => current ? {
      ...current,
      callSessionId: null,
      stateSequence: null,
      endpointDeviceId: null,
      endpointType: null,
      endpointSelectionReason: null,
      destination: normalizedDestination,
      destinationE164: normalizedDestination.type === 'phone_number'
        ? normalizedDestination.e164Number
        : null,
      fromLineId: effectiveLineId || null,
      direction: normalizedDestination.type === 'extension' ? 'INTERNAL' : 'OUTBOUND',
      contactId: dialContext?.contactId ?? null,
      companyId: dialContext?.companyId ?? null,
      dealId: dialContext?.dealId ?? null,
      contactName: dialContext?.contactName ?? null,
      companyName: dialContext?.companyName ?? null,
      callListItemId: dialContext?.callListItemId ?? null,
      state: attempt.prepared || attempt.ready ? 'PREPARING_BROWSER' : 'PREPARING_CALL',
      failureCategory: null,
      failureMessage: null,
      startedAt: null,
      lastEventAt: null,
      answeredAt: null,
      endedAt: null,
      durationSeconds: null,
      notes: null,
      disposition: null,
      afterCallStatus: 'NOT_REQUIRED',
      missedStatus: 'NOT_MISSED',
    } : current);
    let preparationPhase:
      | 'placing'
      | 'registering'
      | 'preparing_provider'
      | 'dispatching' = 'placing';
    try {
      assertAttemptActive();
      const placement = attempt.prepared ?? attempt.ready ??
        await voiceApi.placeCall(placementInput);
      if (isSipDevicePlacement(placement)) attempt.callSessionId = placement.callSessionId;
      else if (isBrowserPlacement(placement)) attempt.prepared = placement;
      else if (isReadyBrowserPlacement(placement)) attempt.ready = placement;
      assertAttemptActive();
      if (!mountedRef.current) {
        await cleanupAttempt(attempt);
        return;
      }
      if (isSipDevicePlacement(placement)) {
        updateSession((current) => applyOriginatedResponse(current, placement));
        await retryPendingPersistence(placement, async () => {
          const retried = await voiceApi.placeCall(placementInput);
          if (!isSipDevicePlacement(retried)) {
            throw new Error('SparkOne changed endpoint type while reconciling the call.');
          }
          return retried;
        });
        return;
      }
      if (!isBrowserPlacement(placement) && !isReadyBrowserPlacement(placement)) {
        throw new Error('Spark VP returned an unsupported call endpoint.');
      }
      updateSession((current) => current ? {
        ...current,
        clientUserRef: placement.clientUserRef,
        state: current.stateSequence === null ? 'PREPARING_BROWSER' : current.state,
        endpointDeviceId: current.endpointDeviceId ?? placement.endpointDeviceId,
        endpointType: current.endpointType ?? placement.endpointType,
        endpointSelectionReason: current.endpointSelectionReason ?? placement.endpointSelectionReason,
      } : current);
      const browserSessionId = isReadyBrowserPlacement(placement)
        ? placement.webrtcSessionId
        : placement.browserSession.sessionId;
      if (isBrowserPlacement(placement)) {
        preparationPhase = 'registering';
        if (!browserRuntime.isSessionActive(browserSessionId)) {
          assertAttemptActive();
          await browserRuntime.start(placement);
          if (!mountedRef.current) {
            await cleanupAttempt(attempt);
            return;
          }
        }
        attempt.browserRuntimeStarted = true;
        assertAttemptActive();
        await waitForBrowserRegistration(browserSessionId, {
          getStatus: voiceApi.getBrowserSessionStatus,
          assertOwner: assertAttemptActive,
        });
      } else if (!browserRuntime.isSessionActive(browserSessionId)) {
        throw new VoicePreparationCancelledError(
          'The persistent Browser registration changed during call preparation.',
        );
      }
      assertAttemptActive();
      if (!mountedRef.current) {
        await cleanupAttempt(attempt);
        return;
      }
      preparationPhase = 'preparing_provider';
      const browserCallInput = originateInput(placement);
      // Browser outbound calling has one architecture: obtain a provider-verified
      // one-time authorization, then originate the SIP INVITE from the owning
      // JsSIP runtime. There is deliberately no managed-callback fallback.
      const feature = selectBrowserOriginatedCallFeature({
        capability: await voiceApi.getBrowserOriginateCapability(),
      });
      if (!feature.selected) {
        throw new VoiceRequestRejectedError(
          'Browser-originated calling is unavailable.',
          409,
          `browser_originate_${feature.reason}`,
        );
      }
      const activationProof =
        browserRuntime.getActivePersistentRegistration()?.activationProof;
      if (!activationProof) {
        throw new VoiceRequestRejectedError(
          'This tab no longer owns the Browser activation lease.',
          403,
          'voice_browser_session_authority_revoked',
        );
      }
      const prepared = await voiceApi.prepareBrowserOriginatedCall(
        browserCallInput,
        activationProof,
      );
      // Record the public binding metadata before local dispatch. This keeps
      // cancellation and a fast authoritative realtime event scoped to this
      // exact one-time authorization while microphone/SIP setup is pending.
      // The intent itself remains local to the runtime call stack.
      attempt.browserOriginated = {
        sessionId: browserSessionId,
        preparedCallId: prepared.preparedCallId,
        correlationId: prepared.correlationId,
      };
      preparationPhase = 'dispatching';
      await browserRuntime.originatePreparedCall(prepared);
      // Local dispatch intentionally changes the softphone from registered
      // to dialing. Registration is therefore no longer a valid predicate
      // here; only ownership and attempt continuity may cancel this call.
      assertAttemptCurrentAndOwned();
      updateSession((current) => current ? {
        ...current,
        state: 'DIALING',
        startedAt: current.startedAt ?? new Date().toISOString(),
        lastEventAt: new Date().toISOString(),
        endpointDeviceId: browserCallInput.endpointDeviceId,
        endpointType: 'browser_webrtc',
        endpointSelectionReason: 'browser_originated_sip_invite',
      } : current);
      return;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'The call could not start.';
      logger.warn('Voice call placement failed', { message });
      const definitive422 =
        error instanceof VoiceRequestRejectedError && error.status === 422;
      const currentAtFailure = sessionRef.current;
      const attemptBrowserPlacement = attempt.prepared ?? attempt.ready;
      const sameActiveAttempt =
        attemptRef.current === attempt && !attempt.cancelled;
      const sameAttemptSession = Boolean(
        sameActiveAttempt &&
        currentAtFailure &&
        currentAtFailure.connectorId === attempt.connectorId &&
        JSON.stringify(currentAtFailure.destination) ===
          JSON.stringify(normalizedDestination) &&
        currentAtFailure.fromLineId === (effectiveLineId || null) &&
        (!attemptBrowserPlacement || (
          currentAtFailure.clientUserRef ===
            attemptBrowserPlacement.clientUserRef &&
          currentAtFailure.endpointDeviceId ===
            attemptBrowserPlacement.endpointDeviceId
        )) &&
        (!attempt.callSessionId ||
          !currentAtFailure.callSessionId ||
          attempt.callSessionId === currentAtFailure.callSessionId),
      );
      const preserveAnswered422 = Boolean(
        definitive422 &&
        sameAttemptSession &&
        currentAtFailure?.state === 'ANSWERED',
      );
      const preserveDifferent422 = Boolean(
        definitive422 &&
        currentAtFailure?.callSessionId &&
        !sameAttemptSession,
      );
      if (
        definitive422 &&
        sameAttemptSession &&
        currentAtFailure?.callSessionId &&
        currentAtFailure.state !== 'ANSWERED' &&
        !attempt.callSessionId
      ) {
        // A signed provisional event may identify the exact call before the
        // phase-two response rejects. Carry that identity into scoped cleanup.
        attempt.callSessionId = currentAtFailure.callSessionId;
      }
      const ownershipLost = message === browserOwnershipMessage;
      const cancelled = error instanceof VoicePreparationCancelledError;
      const definitivelyRejected = Boolean(
        error && typeof error === 'object' && 'definitive' in error && error.definitive === true,
      );
      if (
        ownershipLost ||
        cancelled ||
        preparationPhase === 'registering' ||
        preparationPhase === 'preparing_provider' ||
        (definitivelyRejected &&
          !preserveAnswered422 &&
          !preserveDifferent422) ||
        (preparationPhase === 'dispatching' &&
          Boolean(attempt.browserOriginated) &&
          !attempt.callSessionId)
      ) {
        await cleanupAttempt(attempt);
      }
      if (
        (preserveAnswered422 || preserveDifferent422) &&
        attemptRef.current === attempt
      ) {
        attemptRef.current = null;
      }
      if (cancelled) {
        return;
      }
      updateSession((current) => {
        const reconcileDefinitive422 = Boolean(
          definitive422 &&
          sameAttemptSession &&
          current &&
          current.connectorId === attempt.connectorId &&
          JSON.stringify(current.destination) ===
            JSON.stringify(normalizedDestination) &&
          current.fromLineId === (effectiveLineId || null) &&
          (!attemptBrowserPlacement || (
            current.clientUserRef === attemptBrowserPlacement.clientUserRef &&
            current.endpointDeviceId ===
              attemptBrowserPlacement.endpointDeviceId
          )) &&
          current.state !== 'ANSWERED' &&
          (!attempt.callSessionId ||
            !current.callSessionId ||
            current.callSessionId === attempt.callSessionId),
        );
        if (
          !current ||
          (!reconcileDefinitive422 && current.stateSequence !== null) ||
          error instanceof CrmPersistenceCallMismatchError
        ) return current;
        const now = new Date().toISOString();
        return {
          ...current,
          state: 'FAILED',
          failureCategory: 'ORIGINATE_FAILED',
          failureMessage: message,
          lastEventAt: now,
          endedAt: now,
          afterCallStatus: current.callSessionId && current.callListItemId
            ? 'PENDING'
            : 'NOT_REQUIRED',
        };
      });
      setActionError(message);
    } finally {
      setBusy(false);
    }
  }, [assertBrowserOwner, browserRuntime, canCallExternal, canCallInternal, checkPolicy, cleanupAttempt, destination, dialContext, discardAttempt,
    hasCallPermission, integration, policy, refresh, selectedLineId, updateSession]);

  const setMuted = useCallback((muted: boolean) => {
    const current = sessionRef.current;
    if (
      !integration ||
      !current?.callSessionId ||
      current.endpointType !== 'browser_webrtc' ||
      !['DIALING', 'RINGING', 'ANSWERED'].includes(current.state)
    ) {
      setActionError('Microphone mute is only available for an active Browser phone call.');
      return;
    }
    const activeCallIdentity = browserRuntime.getActiveCallIdentity(current.callSessionId);
    if (!canControlVoiceSession(
      current,
      integration.clientUserRef,
      activeCallIdentity,
      browserRuntime.isCallActive(current.callSessionId),
    )) {
      setActionError('Only the active call participant can control this call.');
      return;
    }
    try {
      browserRuntime.setMuted(current.callSessionId, muted);
      muteErrorCallIdRef.current = null;
      setActionError((message) => message === browserMuteErrorMessage ? null : message);
    } catch {
      muteErrorCallIdRef.current = current.callSessionId;
      setActionError(browserMuteErrorMessage);
    }
  }, [browserRuntime, integration]);

  const hangUp = useCallback(() => {
    if (hangUpPromiseRef.current) return hangUpPromiseRef.current;
    const current = sessionRef.current;
    if (!integration || !current) return Promise.resolve();
    const pendingBrowserOriginated = attemptRef.current?.browserOriginated;
    if (!current.callSessionId && pendingBrowserOriginated) {
      const operation = (async () => {
        setBusy(true);
        setActionError(null);
        await cleanupAttempt(attemptRef.current!);
        const now = new Date().toISOString();
        updateSession((value) => value ? {
          ...value,
          state: 'ENDED',
          endedAt: now,
          lastEventAt: now,
        } : value);
        setBusy(false);
      })().finally(() => { hangUpPromiseRef.current = null; });
      hangUpPromiseRef.current = operation;
      return operation;
    }
    if (!current.callSessionId) return Promise.resolve();
    const activeCallIdentity = browserRuntime.getActiveCallIdentity(current.callSessionId);
    if (!canControlVoiceSession(
      current,
      integration.clientUserRef,
      activeCallIdentity,
      browserRuntime.isCallActive(current.callSessionId),
    )) {
      setActionError('Only the active call participant can control this call.');
      return Promise.resolve();
    }
    const callSessionId = current.callSessionId;
    const operation = (async () => {
      setBusy(true);
      setActionError(null);
      // Always end local call media on operator intent, even when the API
      // gateway cannot confirm the server-side command. A persistent idle
      // registration remains available for the next call.
      await browserRuntime.endCall(callSessionId);
      const outcome = await hangUpWithRecovery({
        connectorId: integration.id,
        callSessionId,
      });
      if (outcome.kind === 'unconfirmed') {
        logger.warn('Voice Hang Up remained unconfirmed after bounded recovery', {
          callSessionId,
          message:
            outcome.error instanceof Error
              ? outcome.error.message
              : String(outcome.error),
        });
        const occurredAt = new Date().toISOString();
        const locallyDegraded = updateSession((session) =>
          session?.callSessionId === callSessionId &&
          isStaleSignalingSession(session)
            ? {
                ...session,
                state: 'FAILED',
                failureCategory: 'STALE_CALL_STATE',
                failureMessage:
                  'The call stopped reporting status and was closed locally after Hang Up could not be confirmed.',
                lastEventAt: occurredAt,
                endedAt: occurredAt,
                afterCallStatus: session.callListItemId ? 'PENDING' : 'NOT_REQUIRED',
              }
            : session,
        );
        if (locallyDegraded?.state === 'FAILED') {
          attemptRef.current = null;
          setActionError(
            'SparkOne locally closed an old stale call record. Verify that the desk phone is disconnected; server status will reconcile when service returns.',
          );
        } else {
          setActionError(
            'Hang Up could not be confirmed. Check that the phone is disconnected, then retry.',
          );
        }
        return;
      }

      attemptRef.current = null;
      if (outcome.kind === 'reconciled') {
        updateSession((session) =>
          session?.callSessionId === callSessionId
            ? sessionFromRecord(outcome.call)
            : session,
        );
        return;
      }

      const degraded = outcome.response.resolution === 'stale_state_degraded';
      const failed = outcome.response.state === 'failed' || degraded;
      const occurredAt = new Date().toISOString();
      updateSession((session) =>
        session?.callSessionId === callSessionId && isActiveSession(session)
          ? {
              ...session,
              state: failed ? 'FAILED' : 'ENDED',
              failureCategory: degraded
                ? 'STALE_CALL_STATE'
                : session.failureCategory,
              failureMessage: degraded
                ? 'The call stopped reporting status and was closed after Hang Up could not be confirmed.'
                : session.failureMessage,
              lastEventAt: occurredAt,
              endedAt: occurredAt,
              afterCallStatus: session.callListItemId ? 'PENDING' : 'NOT_REQUIRED',
            }
          : session,
      );
      if (degraded) {
        setActionError(
          'SparkOne closed a stale call record. Verify that the desk phone is disconnected.',
        );
      }
    })().finally(() => {
      setBusy(false);
      if (hangUpPromiseRef.current === operation) {
        hangUpPromiseRef.current = null;
      }
    });
    hangUpPromiseRef.current = operation;
    return operation;
  }, [browserRuntime, integration, updateSession]);

  const savePostCall = useCallback(async (body: {
    notes?: string;
    disposition?: string;
    callback?: { title?: string; description?: string; dueDate: string; assignedTenantMembershipId?: string };
  }) => {
    if (!canPostCall) {
      setActionError('Your role cannot complete post-call work.');
      return false;
    }
    if (!session?.callSessionId) return false;
    const callSessionId = session.callSessionId;
    setBusy(true);
    setActionError(null);
    try {
      const result = await voiceApi.savePostCall(callSessionId, {
        ...body,
        afterCallStatus: 'COMPLETED',
      });
      if (
        result.call.callSessionId !== callSessionId ||
        sessionRef.current?.callSessionId !== callSessionId
      ) return false;
      updateSession((current) => current?.callSessionId === callSessionId
        ? sessionFromRecord(result.call)
        : current);
      if (attemptRef.current?.callSessionId === callSessionId) {
        attemptRef.current = null;
      }
      return true;
    } catch (error) {
      if (sessionRef.current?.callSessionId === callSessionId) {
        setActionError(error instanceof Error ? error.message : 'Post-call work could not be saved.');
      }
      return false;
    } finally {
      setBusy(false);
    }
  }, [canPostCall, session, updateSession]);

  const completeCallListOutcome = useCallback((callListItemId: string) => {
    const current = sessionRef.current;
    if (
      !current?.callSessionId ||
      current.callListItemId !== callListItemId ||
      !['ENDED', 'FAILED'].includes(current.state)
    ) return;
    dismissedTerminalCallIdsRef.current.add(current.callSessionId);
    updateSession((value) => value?.callListItemId === callListItemId
      ? { ...value, afterCallStatus: 'COMPLETED' }
      : value);
    setDialContext((value) => value?.callListItemId === callListItemId ? null : value);
    setNotification((value) => value?.callSessionId === current.callSessionId ? null : value);
    setActionError(null);
    setDialerOpen(false);
  }, [updateSession]);

  const ownsBrowserPhone = Boolean(
    session?.callSessionId && browserRuntime.isCallActive(session.callSessionId),
  );
  const browserCallMuted = browserRuntime.isMuted;
  const value = useMemo<VoiceWorkspaceContextValue>(() => ({
    canCall, canCallExternal, canCallInternal, hasCallPermission, browserCalling, canPostCall, integration, lines, selectedLineId, setSelectedLineId: updateSelectedLineId, destination, setDestination: updateDestination,
    dialContext, session, activeCalls: Object.values(activeCallsById), policy, policyLoading,
    loadError, actionError, busy, ownsBrowserPhone, isMuted: browserCallMuted, dialerOpen, notification, dismissNotification: () => setNotification(null), openDialer, closeDialer, cancelCallPreparation, checkPolicy,
    startCall, setMuted, hangUp, savePostCall, completeCallListOutcome, refresh,
  }), [actionError, browserCalling, busy, canCall, canCallExternal, canCallInternal, canPostCall, cancelCallPreparation, checkPolicy, closeDialer, destination, dialContext, dialerOpen, hangUp,
    hasCallPermission, integration, lines, loadError, openDialer, policy, policyLoading, refresh, savePostCall, completeCallListOutcome,
    selectedLineId, session, activeCallsById, setMuted, startCall, updateDestination, updateSelectedLineId, notification, ownsBrowserPhone, browserCallMuted]);

  return <VoiceWorkspaceContext.Provider value={value}>{children}</VoiceWorkspaceContext.Provider>;
}

export function useVoiceWorkspace(): VoiceWorkspaceContextValue {
  const context = useContext(VoiceWorkspaceContext);
  if (!context) throw new Error('VoiceWorkspaceProvider is required');
  return context;
}

export function useOptionalVoiceWorkspace(): VoiceWorkspaceContextValue | null {
  return useContext(VoiceWorkspaceContext);
}
